import { css } from '@emotion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { deleteMentorshipProgramGuide } from 'redux/actions/mentoring-program-guides';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { MentoringProgramGuide } from 'redux/schemas/models/mentoring-program-guides';
import prodPathReplace from 'shared/prod-path-rewrite';
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import NvTruncatedTextWithTooltip from 'shared/components/nv-truncated-text-with-tooltip';
import NvUploadedFile from 'shared/components/nv-uploaded-file';
import InlineVideoPreview from 'athena/components/inline-video-preview';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import CreateGuideFlyout from 'athena/components/mentoring-program/guides/create-guide-flyout';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { gray6 } from 'styles/global_defaults/colors';
import { handheld, tablet } from 'styles/global_defaults/media-queries';
import {
  doubleSpacing,
  extraLargeSpacing,
  largeSpacing,
  standardSpacing,
  topHeaderHeight,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';


const doubleModalZIndex = 7050;

type GuideModalProps = {
  mentoringProgramGuide: MentoringProgramGuide;
};

const styles = (
  isScrolled: boolean,
  headerHeight: number,
) => css`
  .guide-header {
    transition: height 0.3s ease, background-size 0.3s ease;

    ${isScrolled ? css`
      height: 80px;
      align-items: center;
      justify-content: left;
      border-bottom: 1px solid ${gray6};
      background: linear-gradient(
        90deg,
        rgba(235, 179, 129, 0.1) 0%,
        rgba(147, 218, 225, 0.1) 100%
      );
    ` : css`
      background: url('${prodPathReplace('images/program-guide-bg.svg')}') no-repeat top center;
      height: 100%;
      justify-content: center;
      background-size: contain;
    `}
  }

  .guide-title-wrapper {
    height: fit-content;
  }

  .guide-title {
    width: unset;
    max-height: fit-content;
    line-height: unset;
    ${isScrolled ? css`
      margin: 0 65px 0 ${doubleSpacing}px;
    ` : css`
      text-align: center;
      margin: ${tripleSpacing}px ${extraLargeSpacing}px ${largeSpacing}px;
    `}
    transition: all 0.3s ease;
  }

  .options-menu {
    top: ${standardSpacing}px;
    right: ${standardSpacing}px;
  }

  .guide-content {
    top: ${isScrolled ? '80px' : `${headerHeight}px`};
    height: calc(100% - ${isScrolled ? topHeaderHeight : 0}px - ${headerHeight}px);
    overflow-y: auto;
  }

  .guide-content-body {
    box-shadow: ${isScrolled ? 'none' : '0px 4px 10px 0px rgba(0, 0, 0, 0.1)'};
    gap: ${largeSpacing}px;
    border-radius: ${standardSpacing}px;
    transition: all 0.3s ease;

    margin: 0 95px;
    ${tablet(css`
      margin: 0 ${largeSpacing}px;
    `)}
    ${handheld(css`
      margin: 0 ${standardSpacing}px;
    `)}
  }
`;

const GuideModal = ({ mentoringProgramGuide }: GuideModalProps) => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { injectServices } = useContext(AngularContext);

  const [$state] = injectServices(['$state']);
  const { isAdmin } = useMentoringProgramRole();

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  const [showCreateGuideFlyout, setShowCreateGuideFlyout] = useState(false);
  const [guideFlyoutDefaultValues, setGuideFlyoutDefaultValues] = useState(null);

  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.GUIDES;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.guidance.modal;

  const { title, content, video, attachments } = mentoringProgramGuide || {};

  const options: NvDropdownOption[] = [
    {
      type: 'text',
      text: translationBase.OPTIONS.EDIT(),
      callback: () => editGuide(),
    },
    {
      type: 'text',
      text: translationBase.OPTIONS.MAKE_A_COPY(),
      callback: () => copyGuide(),
    },
    {
      type: 'divider',
    },
    {
      type: 'text',
      text: translationBase.OPTIONS.DELETE(),
      textClassName: 'color-danger',
      callback: () => deleteGuide(),
    },
  ];

  const editGuide = () => {
    setGuideFlyoutDefaultValues(mentoringProgramGuide);
    setShowCreateGuideFlyout(true);
  };

  const copyGuide = () => {
    const { id, ...rest } = mentoringProgramGuide;
    setGuideFlyoutDefaultValues(rest);
    setShowCreateGuideFlyout(true);
  };

  const deleteGuide = () => {
    dispatch(openConfirmationDialog({
      title: translationBase.OPTIONS.DELETE_GUIDE_DIALOG.TITLE(),
      bodyText: translationBase.OPTIONS.DELETE_GUIDE_DIALOG.BODY(),
      confirmText: t.FORM.DELETE(),
      onConfirm: () => {
        wrapThunkAction(dispatch(deleteMentorshipProgramGuide({
          mentoringProgramId: mentoringProgram.id,
          guideId: mentoringProgramGuide.id,
        }))).then(() => {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: translationBase.OPTIONS.GUIDE_DELETED_SUCCESS(),
          }));
          resetGuideIdParam();
          if ($state.current.name !== 'mentoring-program-guidance') {
            $state.go('mentoring-program-guidance', {
              programId: mentoringProgram.id,
            });
          }
        });
      },
    }));
  };

  const resetGuideIdParam = () => {
    const params = { guideId: undefined };
    $state.go($state.current, params, { notify: false });
  };

  useEffect(() => {
    const bodyEl = document.querySelector('.guide-content');
    if (!bodyEl) return null;
    const handleScroll = () => {
      setIsScrolled((bodyEl as HTMLElement).scrollTop > 20);
    };
    bodyEl.addEventListener('scroll', handleScroll);
    return () => {
      bodyEl.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!headerRef.current) return null;
    const el = headerRef.current;
    const observer = new ResizeObserver(() => {
      setHeaderHeight(el.offsetHeight);
    });
    observer.observe(el);
    setHeaderHeight(el.offsetHeight);

    return () => observer.disconnect();
  }, []);

  return (
    <NvModal
      show
      doubleModal
      fullHeight={false}
      type={ModalType.NO_DIALOG}
      closeDataQa={dataQATag.close}
      onClose={resetGuideIdParam}
      closeOnBackdropClick
      body={(
        <div
          css={styles(isScrolled, headerHeight)}
          className='modal-page'
        >
          <div className='d-flex guide-header'>
            <div ref={headerRef} className='guide-title-wrapper'>
              <NvTruncatedTextWithTooltip
                zIndex={doubleModalZIndex}
                className={`align-items-start ${isScrolled ? 'text-xl' : 'header-xl'} font-weight-light guide-title`}
                linesCount={isScrolled ? 1 : 2}
                text={title}
              />
            </div>
            {isAdmin && (
              <div className='position-absolute options-menu'>
                <NvDropdown
                  buttonStyle={NvDropdownButtonStyle.CUSTOM}
                  align={NvDropdownAlign.RIGHT}
                  items={options}
                  customTarget={() => (
                    <div>
                      <NvIcon
                        icon='edit'
                        size='small'
                        className='color-gray-3'
                      />
                    </div>
                  )}
                />
              </div>
            )}
          </div>
          <div className='position-absolute pt-2 pb-6 guide-content w-100'>
            <div className='d-flex flex-column p-6 bg-color-white guide-content-body'>
              <div
                className='text-large-body mb-0'
                dangerouslySetInnerHTML={{
                  __html: content,
                }}
              />
              {video && (
                <InlineVideoPreview
                  videoSrc={video.url}
                  title={video.fileName}
                  thumbnailSrc={video.thumbnailUrl}
                  dataQa={dataQATag.videoPlay}
                  dataQaId={`${dataQATag.videoPlay}-${video.fileName}`}
                  ariaLabel={translationBase.ARIA_LABEL.PLAY_VIDEO(
                    video.fileName
                  )}
                />
              )}
              {attachments?.length ? (
                <div>
                  <div className='text-small semi-bold mb-2'>
                    {translationBase.RELATED_MATERIALS()}
                  </div>
                  <ul className='list-unstyled'>
                    {attachments.map(({
                      fileName: name,
                      fileSize: size,
                      fileType: type,
                      url,
                    }) => (
                      <li key={name} className='mb-1'>
                        <NvUploadedFile
                          file={{
                            uniqueId: `attachment-${name}`,
                            name,
                            size,
                            type,
                            url,
                          }}
                          displayTopOptions={false}
                          displayInline={false}
                          openModalOnClick={false}
                          downloadOnClick
                          downloadDataQa={dataQATag.attachmentDownload}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
          {showCreateGuideFlyout && (
            <CreateGuideFlyout
              onClose={(shouldReset) => {
                setShowCreateGuideFlyout(false);
                setGuideFlyoutDefaultValues(null);
                // TODO: revisit this when working on the context-preservation card (NOV-101868)
                // Determine if we need to reset (and how that would work), navigation, etc.
                // if (shouldReset) {
                //   reset();
                // }
              }}
              defaultValues={guideFlyoutDefaultValues}
            />
          )}
        </div>
      )}
    />
  );
};

export default GuideModal;
